import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { PortalLayoutComponent } from './layout/portal-layout.component';
import { TopbarComponent } from './layout/topbar.component';

import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DashboardAdminComponent } from './pages/admin/admin-dashboard.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { UserDialogComponent } from './pages/users/user-dialog/user-dialog.component';
import { RoleDialogComponent } from './pages/roles/role-dialog/role-dialog.component';
import { PermissionDialogComponent } from './pages/roles/permission-dialog/permission-dialog.component';
import { DeliveryComponent } from './pages/logistics/deliveries/delivery.component';
import { DeliveryDialogComponent } from './pages/logistics/deliveries/delivery-dialog/delivery-dialog.component';
import { RiderDialogComponent } from './pages/logistics/riders/rider-dialog/rider-dialog.component';
import { RidersComponent } from './pages/logistics/riders/riders.component';
import { VehicleComponent } from './pages/logistics/vehicles/vehicle.component';
import { VehicleDialogComponent } from './pages/logistics/vehicles/vehicle-dialog/vehicle-dialog.component';
import { AssignVehicleDialogComponent } from './pages/logistics/vehicles/assign-vehicle-dialog/assign-vehicle-dialog.component';
import { MaintenanceComponent } from './pages/logistics/maintenance/maintenance.component';
import { ActivityComponent } from './pages/logistics/activities/activity.component';
import { ActivityDialogComponent } from './pages/logistics/activities/activity-dialog/activity-dialog.component';
import { LogisticsOverviewComponent } from './pages/logistics/overview/logistics-overview.component';
import { LogisticsControlTowerComponent } from './pages/logistics/control-tower/logistics-control-tower.component';
import { LogisticsClientsComponent } from './pages/logistics/clients/logistics-clients.component';
import { ClientMessageDialogComponent } from './pages/logistics/clients/client-message-dialog/client-message-dialog.component';
import { LogisticsTrackingComponent } from './pages/logistics/tracking/logistics-tracking.component';
import { ReportsComponent } from './pages/reports/reports.component';
import { StaffComponent } from './pages/staff/staff.component';
import { StaffDialogComponent } from './pages/staff/staff-dialog/staff-dialog.component';
import { StaffRequestsComponent } from './pages/staff/staff-requests/staff-requests.component';
import { StaffMessagesComponent } from './pages/staff/staff-messages/staff-messages.component';
import { StaffPayrollComponent } from './pages/staff/payroll/staff-payroll.component';
import { StaffOverviewComponent } from './pages/staff/overview/staff-overview.component';
import { StaffOperationsComponent } from './pages/staff/operations/staff-operations.component';
import { StaffPerformanceComponent } from './pages/staff/performance/staff-performance.component';
import { CommunicationChatsComponent } from './pages/communication/chats/communication-chats.component';
import { CommunicationCallsComponent } from './pages/communication/calls/communication-calls.component';
import { SupplyProductComponent } from './pages/supply/product/supply-product.component';
import { SupplyDispatchComponent } from './pages/supply/dispatch/supply-dispatch.component';
import { SupplyHistoryAnalyticsComponent } from './pages/supply/history-analytics/supply-history-analytics.component';
import { SupplyOverviewComponent } from './pages/supply/overview/supply-overview.component';
import { SupplyControlTowerComponent } from './pages/supply/control-tower/supply-control-tower.component';
import { SupplyCatalogComponent } from './pages/supply/catalog/supply-catalog.component';
import { ProjectsComponent } from './pages/projects/projects/projects.component';
import { ProjectOnboardingComponent } from './pages/projects/onboarding/project-onboarding.component';
import { ProjectTasksComponent } from './pages/projects/tasks/project-tasks.component';
import { TaskCreateDialogComponent } from './pages/projects/tasks/task-create-dialog/task-create-dialog.component';
import { ProjectAnalyticsComponent } from './pages/projects/analytics/project-analytics.component';
import { ProjectTodoComponent } from './pages/projects/todo/project-todo.component';
import { TodoPageComponent } from './pages/todo/todo-page.component';
import { TodoDetailDialogComponent } from './pages/todo/todo-detail-dialog/todo-detail-dialog.component';
import { TodoCreateDialogComponent } from './pages/todo/todo-create-dialog/todo-create-dialog.component';
import { ProjectOverviewComponent } from './pages/projects/overview/project-overview.component';
import { ProjectTimelineComponent } from './pages/projects/timeline/project-timeline.component';
import { ProjectDocumentsComponent } from './pages/projects/documents/project-documents.component';
import { DocumentCreateDialogComponent } from './pages/projects/documents/document-create-dialog/document-create-dialog.component';
import { ProjectRisksComponent } from './pages/projects/risks/project-risks.component';
import { MeetingsPageComponent } from './pages/meetings/meetings-page.component';
import { MeetingCreateDialogComponent } from './pages/meetings/meeting-create-dialog/meeting-create-dialog.component';
import { MeetingDetailDialogComponent } from './pages/meetings/meeting-detail-dialog/meeting-detail-dialog.component';
import { MeetingRoomComponent } from './pages/meetings/meeting-room.component';
import { TutorialDialogComponent } from './shared/tutorial-dialog/tutorial-dialog.component';
import { AutoDatePickerDirective } from './shared/directives/auto-date-picker.directive';




import { AuthModule } from './pages/auth/auth.module';

import { AuthService } from './core/auth.service';
import { AuthGuard } from './core/auth.guard';
import { RoleGuard } from './core/role.guard';
import { TokenInterceptor } from './core/token.interceptor';

import { InMemoryDataService } from './mock-backend/in-memory-data.service';
import { UsersComponent } from './pages/users/users.component';
import { RolesComponent } from './pages/roles/roles.component';

import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';

import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';


import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatBadgeModule } from '@angular/material/badge';


@NgModule({
  declarations: [
    AppComponent,
    PortalLayoutComponent,
    TopbarComponent,
    DashboardComponent,
    DashboardAdminComponent,
    UnauthorizedComponent,
    UsersComponent,
    RolesComponent,
    UserDialogComponent,
    RoleDialogComponent,
    PermissionDialogComponent,
    DeliveryComponent,
    DeliveryDialogComponent,
    LogisticsOverviewComponent,
    LogisticsControlTowerComponent,
    LogisticsTrackingComponent,
    LogisticsClientsComponent,
    ClientMessageDialogComponent,
    RiderDialogComponent,
    RidersComponent,
    VehicleComponent,
    VehicleDialogComponent,
    AssignVehicleDialogComponent,
    MaintenanceComponent,
    ActivityComponent,
    ActivityDialogComponent,
    ReportsComponent,
    StaffComponent,
    StaffDialogComponent,
    StaffRequestsComponent,
    StaffMessagesComponent,
    StaffPayrollComponent,
    StaffOverviewComponent,
    StaffOperationsComponent,
    StaffPerformanceComponent,
    CommunicationChatsComponent,
    CommunicationCallsComponent,
    SupplyProductComponent,
    SupplyDispatchComponent,
    SupplyHistoryAnalyticsComponent,
    SupplyOverviewComponent,
    SupplyControlTowerComponent,
    SupplyCatalogComponent,
    ProjectsComponent,
    ProjectOnboardingComponent,
    ProjectTasksComponent,
    TaskCreateDialogComponent,
    ProjectAnalyticsComponent,
    ProjectTodoComponent,
    TodoPageComponent,
    TodoDetailDialogComponent,
    TodoCreateDialogComponent,
    ProjectOverviewComponent,
    ProjectTimelineComponent,
    ProjectDocumentsComponent,
    DocumentCreateDialogComponent,
    ProjectRisksComponent,
    MeetingsPageComponent,
    MeetingCreateDialogComponent,
    MeetingDetailDialogComponent,
    MeetingRoomComponent,
    TutorialDialogComponent,
    AutoDatePickerDirective,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    HttpClientInMemoryWebApiModule.forRoot(InMemoryDataService, { delay: 400, passThruUnknownUrl: true }),
    FormsModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatTabsModule,
    AuthModule,
    AppRoutingModule,
    MatTableModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule, // ✅ Add this
    MatOptionModule, // ✅ And this
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    FormsModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatBadgeModule,
  ],
  providers: [
    AuthService,
    AuthGuard,
    RoleGuard,
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
